import React, { useState, useEffect } from "react";
import { Box, Button, Stack, useMediaQuery, CircularProgress, Grid, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { getSearchingProperty, selectInvitationState } from "../../sagas/invitations/invitationSlice";
import { ResidentpageState } from "../../ResidentpageState";
import { InputField } from "../../../../components/InputController";
import CustomDropdown from "../../../../components/customDropdown";
import ExtraLinks from "./ExtraLinks";
import { approvedISOStandards } from "../../../util/approvedISOStandards";

const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

interface PropsType {
  setPageState: React.Dispatch<React.SetStateAction<ResidentpageState>>;
}

const defaultAddressState = {
  propertyName: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

const useStyles = makeStyles({
  buttonProgress: {
    color: "#053742",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  text: {
    fontFamily: "Work Sans",
    fontSize: "18px !important",
    margin: "17px 0 !important",
  },
});

export default function SearchPropertiesByAddress(props: PropsType) {
  const classes = useStyles();
  const matchesMobile = useMediaQuery("(max-width:380px)");

  // Handle addressState: start
  const [addressState, setAddressState] = useState(defaultAddressState);
  const handleAddressState = (e: any) => {
    setAddressState({
      ...addressState,
      [e.target.name]: e.target.value,
    });
  };
  // Handle addressState: end

  // Form Validation: start
  const [isValidForm, setIsValidForm] = useState(false);
  useEffect(() => {
    if ((addressState.address || addressState.city || addressState.zip || addressState.state) && addressState.country) {
      setIsValidForm(true);
    } else {
      setIsValidForm(false);
    }
  }, [addressState]);
  // Form Validation: end

  // Handle Submit: start
  const dispatch = useDispatch();
  const searchPropertiesByAddress = () => {
    dispatch(
      getSearchingProperty({
        PropertyName: undefined,
        PhysicalAddress: addressState.address  || undefined,
        City: addressState.city || undefined,
        State: addressState.state || undefined,
        Zip: addressState.zip || undefined,
        Country: addressState.country,
      })
    );
  };
  // Handle Submit: end

  // Check submission: start
  const { isSearchingProperty, isSearchingPropertySucceed, propertyResponse } = useSelector(selectInvitationState);
  useEffect(() => {
    if (isSearchingPropertySucceed && propertyResponse?.properties?.length) {
      props.setPageState(ResidentpageState.SelectProperty);
    }
  }, [isSearchingPropertySucceed, propertyResponse]);
  // Check submission: end

  return (
    <Stack direction="column" justifyContent="center" alignItems="center" spacing={4} sx={{ marginTop: "10px" }}>
      <Typography className={classes.text}>Please enter a few details so we can find your property.</Typography>

      <Box sx={{ marginTop: "0px !important", width: "100%" }}>
        <InputField name="address" placeholder="e.g. 2487" label="Street Number" value={addressState.address} onChange={handleAddressState} />
      </Box>

      <Grid
        container
        direction="row"
        spacing={1}
        sx={{
          marginTop: "-8px !important",
          width: "calc(100% + 8px)",
          marginRight: "8px !important",
        }}
      >
        <Grid item xs={12} sm={6}>
          <InputField name="city" placeholder="City" label="City" value={addressState.city} onChange={handleAddressState} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomDropdown
            id={"state-dropdown"}
            isFullWidth
            label={"State"}
            labelId={""}
            value={addressState.state}
            handleChange={(e) =>
              setAddressState({
                ...addressState,
                state: e.target.value,
              })
            }
          >
            {addressState.country === "US" ? (
              approvedStates.map((stateRecord) => (
                <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                  {stateRecord.State + " - " + stateRecord.Code}
                </MenuItem>
              ))
            ) : addressState.country === "CA" ? (
              approvedProvinces.map((provinceRecord) => (
                <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                  {provinceRecord.Province + " - " + provinceRecord.Code}
                </MenuItem>
              ))
            ) : (
              <MenuItem style={{ height: "36px" }} value="NA">
                other
              </MenuItem>
            )}
          </CustomDropdown>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        spacing={1}
        sx={{
          marginTop: "-8px !important",
          width: "calc(100% + 8px)",
          marginRight: "8px !important",
        }}
      >
        <Grid item xs={12} sm={6}>
          <InputField name="zip" placeholder="Zip" label="Zip" value={addressState.zip} onChange={handleAddressState} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomDropdown
            id={"country-dropdown"}
            isFullWidth
            label={"Country"}
            labelId={""}
            value={addressState.country}
            handleChange={(e) => {
              setAddressState({
                ...addressState,
                state: e.target.value === "US" ? approvedStates[0].Code : e.target.value === "CA" ? approvedProvinces[0].Code : "NA",
                country: e.target.value,
              });
            }}
          >
            {approvedCountries.map((countryRecord) => (
              <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                {countryRecord.Country + " - " + countryRecord.Code}
              </MenuItem>
            ))}
          </CustomDropdown>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", flexDirection: matchesMobile ? "column" : "row", marginTop: "12px !important" }}>
        <Button
          variant="outlined"
          sx={{
            width: matchesMobile ? "100%" : "183px",
            height: "49px",
            fontSize: "18px",
            borderRadius: "24px",
            textTransform: "none",
            color: "#39A2DB",
            transition: "background 0.55s, color 0.55s",
            borderColor: "#39A2DB",
            "&:hover": {
              backgroundColor: "#39A2DB",
              color: "white",
              borderColor: "#39A2DB",
            },
          }}
          onClick={() => {
            props.setPageState(ResidentpageState.SearchResidentByEmail);
          }}
        >
          Search by email
        </Button>
        <Button
          type="submit"
          variant="contained"
          style={{
            width: matchesMobile ? "100%" : "113px",
            marginTop: matchesMobile ? "20px" : "",
            height: "49px",
            fontSize: "18px",
            borderRadius: "24px",
            textTransform: "capitalize",
            color: "white",
            backgroundColor: isValidForm ? "#39A2DB" : "rgba(211, 211, 211, 1)",
            borderColor: isValidForm ? "#39A2DB" : "rgba(211, 211, 211, 1)",
          }}
          onClick={searchPropertiesByAddress}
          disabled={!isValidForm}
        >
          Search
          {isSearchingProperty ? <CircularProgress size={26} className={classes.buttonProgress} /> : <></>}
        </Button>
      </Stack>

      <ExtraLinks />
    </Stack>
  );
}
